import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Scale, CheckCircle2, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import SOPFlowchart from '../components/SOPFlowchart';

function RecipeDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [limitingAmount, setLimitingAmount] = useState('');
  const [scaledIngredients, setScaledIngredients] = useState([]);
  const [isScaled, setIsScaled] = useState(false);
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchData, setBatchData] = useState({ actual_yield: '', notes: '' });
  const [submittingBatch, setSubmittingBatch] = useState(false);

  useEffect(() => {
    fetchRecipe();
  }, [id]);

  const fetchRecipe = async () => {
    try {
      const response = await axios.get(`${API}/recipes/${id}`);
      setRecipe(response.data);
    } catch (error) {
      toast.error('Failed to fetch recipe');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleScale = () => {
    if (!limitingAmount || !recipe.limiting_ingredient_name) {
      toast.error('Please enter the available amount');
      return;
    }

    const limitingIngredient = recipe.ingredients.find(
      (ing) => ing.name === recipe.limiting_ingredient_name
    );

    if (!limitingIngredient) {
      toast.error('Limiting ingredient not found');
      return;
    }

    const scaleFactor = parseFloat(limitingAmount) / limitingIngredient.quantity;

    const scaled = recipe.ingredients.map((ing) => ({
      ...ing,
      scaled_quantity: (ing.quantity * scaleFactor).toFixed(2),
    }));

    setScaledIngredients(scaled);
    setIsScaled(true);
    toast.success('Recipe scaled successfully!');
  };

  const handleRecordBatch = () => {
    if (!isScaled) {
      toast.error('Please scale the recipe first');
      return;
    }
    setShowBatchDialog(true);
  };

  const submitBatch = async (recordNow) => {
    setSubmittingBatch(true);

    try {
      const totalYield = scaledIngredients.reduce(
        (sum, ing) => sum + parseFloat(ing.scaled_quantity),
        0
      );

      const batchPayload = {
        recipe_id: recipe.id,
        scaled_ingredients: scaledIngredients.map((ing) => ({
          name: ing.name,
          quantity: parseFloat(ing.scaled_quantity),
          unit: ing.unit,
          percentage: ing.percentage,
          is_limiting: ing.is_limiting,
        })),
        expected_yield: parseFloat(totalYield.toFixed(2)),
        actual_yield: recordNow ? parseFloat(batchData.actual_yield) : null,
        notes: batchData.notes || null,
      };

      await axios.post(`${API}/batches`, batchPayload);
      toast.success(recordNow ? 'Batch recorded!' : 'Batch logged for later entry');
      setShowBatchDialog(false);
      setBatchData({ actual_yield: '', notes: '' });
      setIsScaled(false);
      setLimitingAmount('');
      setScaledIngredients([]);
    } catch (error) {
      toast.error('Failed to record batch');
    } finally {
      setSubmittingBatch(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (!recipe) return null;

  const ingredientsToShow = isScaled ? scaledIngredients : recipe.ingredients;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 pb-24">
      {/* Header */}
      <header className="glass border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/')}
            data-testid="back-button"
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-bold text-lg text-gray-900">{recipe.name}</h1>
            <p className="text-xs text-gray-500">{recipe.category}</p>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Recipe Type Badge */}
        <div className="mb-4">
          <span className={`inline-block px-4 py-2 rounded-full text-sm font-semibold ${
            recipe.recipe_type === 'assembly' 
              ? 'bg-accent-coral/20 text-accent-coral'
              : 'bg-primary/20 text-primary'
          }`}>
            {recipe.recipe_type === 'assembly' ? '🍽️ Assembly Recipe' : '⚗️ Batch Prep Recipe'}
          </span>
        </div>

        {/* Scaling Section - Only for Batch Prep */}
        {recipe.recipe_type === 'batch_prep' && recipe.limiting_ingredient_name && (
          <div className="bg-white rounded-2xl p-6 shadow-md mb-6 border border-primary/20">
            <div className="flex items-center gap-2 mb-4">
              <Scale className="h-5 w-5 text-primary" />
              <h2 className="font-bold text-lg text-gray-900">Scale Recipe</h2>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="limiting-amount" className="text-sm font-medium text-gray-700">
                  Available {recipe.limiting_ingredient_name}
                </Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    id="limiting-amount"
                    data-testid="limiting-ingredient-input"
                    type="number"
                    step="0.01"
                    placeholder="Enter amount"
                    value={limitingAmount}
                    onChange={(e) => setLimitingAmount(e.target.value)}
                    className="flex-1 h-12 rounded-xl"
                  />
                  <Button
                    onClick={handleScale}
                    data-testid="scale-recipe-button"
                    className="h-12 px-6 bg-primary hover:bg-primary-light text-white font-semibold rounded-xl transition-colors"
                  >
                    Calculate
                  </Button>
                </div>
              </div>
              {isScaled && (
                <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-xl">
                  <CheckCircle2 className="h-5 w-5" />
                  <span className="text-sm font-medium">Recipe scaled successfully</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Ingredients - Only for Batch Prep */}
        {recipe.recipe_type === 'batch_prep' && (
          <div className="bg-white rounded-2xl p-6 shadow-md mb-6">
            <h2 className="font-bold text-lg text-gray-900 mb-4">Ingredients</h2>
            <div className="space-y-3">
              {ingredientsToShow.map((ing, idx) => (
                <div
                  key={idx}
                  data-testid={`ingredient-${idx}`}
                  className={`flex items-center justify-between p-3 rounded-xl ${
                    ing.name === recipe.limiting_ingredient_name
                      ? 'bg-primary/10 border border-primary/30'
                      : 'bg-gray-50'
                  }`}
                >
                  <div className="flex-1">
                    <span className="font-medium text-gray-900">{ing.name}</span>
                    {ing.name === recipe.limiting_ingredient_name && (
                      <span className="ml-2 text-xs px-2 py-1 rounded-full bg-primary text-white">Limiting</span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-gray-900">
                      {isScaled ? ing.scaled_quantity : ing.quantity.toFixed(2)} {ing.unit}
                    </span>
                    {ing.percentage && (
                      <span className="block text-xs text-gray-500">{ing.percentage}%</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SOP Flowchart */}
        <div className="bg-gradient-to-br from-primary/5 to-white rounded-2xl p-6 shadow-md mb-6">
          <h2 className="font-bold text-xl text-gray-900 mb-6 flex items-center gap-2">
            <div className="w-2 h-8 bg-gradient-to-b from-primary to-primary-light rounded-full"></div>
            {recipe.recipe_type === 'assembly' ? 'Assembly Steps' : 'Preparation Flow'}
          </h2>
          <SOPFlowchart steps={recipe.sop_steps} />
        </div>

        {/* Notes */}
        {recipe.notes && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
            <h3 className="font-semibold text-amber-900 mb-2">Notes</h3>
            <p className="text-amber-800 text-sm">{recipe.notes}</p>
          </div>
        )}
      </main>

      {/* Floating Action Button - Only for Batch Prep */}
      {recipe.recipe_type === 'batch_prep' && (
        <div className="fixed bottom-6 left-0 right-0 px-4 z-50">
          <div className="max-w-4xl mx-auto">
            <Button
              onClick={handleRecordBatch}
              data-testid="record-batch-button"
              className="w-full h-14 bg-primary hover:bg-primary-light text-white font-bold rounded-2xl shadow-xl transition-colors"
            >
              Record as Batch
            </Button>
          </div>
        </div>
      )}

      {/* Batch Dialog */}
      <Dialog open={showBatchDialog} onOpenChange={setShowBatchDialog}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle>Record Batch</DialogTitle>
            <DialogDescription>Enter the actual yield or save for later entry</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="actual-yield">Actual Yield (grams)</Label>
              <Input
                id="actual-yield"
                data-testid="actual-yield-input"
                type="number"
                step="0.01"
                placeholder="Optional - enter now or later"
                value={batchData.actual_yield}
                onChange={(e) => setBatchData({ ...batchData, actual_yield: e.target.value })}
                className="h-12 mt-2 rounded-xl"
              />
            </div>
            <div>
              <Label htmlFor="batch-notes">Notes (Optional)</Label>
              <textarea
                id="batch-notes"
                data-testid="batch-notes-input"
                placeholder="Any observations..."
                value={batchData.notes}
                onChange={(e) => setBatchData({ ...batchData, notes: e.target.value })}
                className="w-full h-24 mt-2 px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button
                onClick={() => submitBatch(false)}
                data-testid="save-for-later-button"
                variant="outline"
                className="flex-1 h-12 rounded-xl font-semibold"
                disabled={submittingBatch}
              >
                <Clock className="h-4 w-4 mr-2" />
                Save for Later
              </Button>
              <Button
                onClick={() => submitBatch(true)}
                data-testid="record-now-button"
                className="flex-1 h-12 bg-gradient-to-r from-purple-600 to-purple-500 hover:from-purple-700 hover:to-purple-600 text-white font-semibold rounded-xl"
                disabled={!batchData.actual_yield || submittingBatch}
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Record Now
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RecipeDetail;